import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaComments } from "react-icons/fa";

// const API_BASE = "https://hopeful-cosmic-koala.ngrok-free.app/api";
const API_BASE = "/api";

// ── Helpers ────────────────────────────────────────────────────────────────
const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const readTime = (text) =>
  `${Math.max(1, Math.ceil((text || "").split(" ").length / 200))} min read`;

// Extract plain text from Quill Delta JSON, trimmed to 200 chars
const extractDescription = (descriptionJson) => {
  try {
    const parsed = JSON.parse(descriptionJson);
    const full = parsed.ops
      .map((op) => op.insert)
      .join("")
      .replace(/\n+/g, " ")
      .trim();
    return full.length > 200 ? full.substring(0, 200) + "..." : full;
  } catch {
    return "";
  }
};

// ── Component ──────────────────────────────────────────────────────────────
const BlogList = () => {
  const [allTags, setAllTags]           = useState([]);
  const [blogs, setBlogs]               = useState([]);
  const [page, setPage]                 = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [loading, setLoading]           = useState(true);
  const [loadingMore, setLoadingMore]   = useState(false);
  const [error, setError]               = useState(null);
  const [activeTag, setActiveTag]       = useState(null);
  const [search, setSearch]             = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch tags once
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await fetch(`${API_BASE}/blog-tags/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "69420",
          },
        });
        if (!response.ok) throw new Error(`Failed to fetch tags: ${response.status}`);
        const data = await response.json();
        setAllTags(Array.isArray(data) ? data : (data.results ?? []));
      } catch (error) {
        console.error("Tags fetch error:", error);
      }
    };
    fetchTags();
  }, []);

  // Fetch blogs when filter/search/page changes
  const fetchBlogs = useCallback(
    async (pg, append = false) => {
      append ? setLoadingMore(true) : setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({ page: pg, page_size: 12 });
        if (debouncedSearch) params.set("q", debouncedSearch);
        if (activeTag) params.set("tag", activeTag);

        const response = await fetch(`${API_BASE}/blogs/?${params}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "69420",
          },
        });

        if (!response.ok)
          throw new Error(`Failed to fetch blogs: ${response.status} ${response.statusText}`);

        const data = await response.json();
        const results = Array.isArray(data) ? data : (data.results ?? []);
        const pages   = data.total_pages ?? 1;

        setBlogs((prev) => (append ? [...prev, ...results] : results));
        setTotalPages(pages);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [debouncedSearch, activeTag]
  );

  useEffect(() => {
    setPage(1);
    fetchBlogs(1, false);
  }, [debouncedSearch, activeTag, fetchBlogs]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchBlogs(next, true);
  };

  const featured  = blogs[0] || null;
  const secondary = blogs.slice(1);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800&display=swap');
        :root {
          --navy:#0b1c6b;--sky:#159EEC;--lsky:#e8f4fd;--dark:#111827;--mid:#6b7280;
          --light:#f9fafb;--white:#ffffff;--border:#e5e7eb;
          --sh-sm:0 2px 8px rgba(11,28,107,0.07);--sh-md:0 8px 30px rgba(11,28,107,0.12);
          --sh-lg:0 24px 60px rgba(11,28,107,0.16);
        }
        .bl *{box-sizing:border-box;margin:0;padding:0;}
        .bl{font-family:'Lexend',sans-serif;color:var(--dark);background:var(--white);overflow-x:hidden;}
        .bl-hero{position:relative;min-height:420px;background:url('/images/work3.jpeg') center/cover no-repeat;display:flex;align-items:center;justify-content:center;overflow:hidden;}
        .bl-hero::before{content:'';position:absolute;inset:0;background:linear-gradient(135deg,rgba(11,28,107,0.94),rgba(21,158,236,0.80));}
        .bl-hero-content{position:relative;z-index:1;text-align:center;color:#fff;padding:80px 20px 60px;width:100%;}
        .bl-badge{display:inline-block;background:rgba(255,255,255,0.13);border:1px solid rgba(255,255,255,0.28);font-size:0.7rem;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;padding:6px 18px;border-radius:50px;margin-bottom:18px;color:#fff;}
        .bl-hero-content h1{font-size:clamp(2.4rem,5vw,3.8rem);font-weight:800;line-height:1.1;margin-bottom:14px;}
        .bl-hero-content p{font-size:1rem;color:rgba(255,255,255,0.8);font-weight:300;max-width:520px;margin:0 auto 36px;line-height:1.8;}
        .bl-search-wrap{max-width:540px;margin:0 auto;display:flex;align-items:center;background:#fff;border-radius:50px;overflow:hidden;box-shadow:0 8px 40px rgba(0,0,0,0.2);}
        .bl-search-wrap input{flex:1;border:none;outline:none;padding:15px 22px;font-family:'Lexend',sans-serif;font-size:0.92rem;color:var(--dark);background:transparent;}
        .bl-search-icon{padding:0 20px;color:var(--sky);font-size:1.1rem;}
        .bl-tags-bar{background:var(--white);border-bottom:1px solid var(--border);position:sticky;top:0;z-index:10;}
        .bl-tags-inner{max-width:1200px;margin:0 auto;padding:16px 24px;display:flex;gap:10px;flex-wrap:wrap;align-items:center;}
        .bl-tag-btn{padding:7px 16px;border-radius:50px;font-family:'Lexend',sans-serif;font-size:0.78rem;font-weight:600;cursor:pointer;transition:all 0.2s;border:1.5px solid var(--border);background:var(--white);color:var(--mid);}
        .bl-tag-btn:hover{border-color:var(--sky);color:var(--sky);}
        .bl-tag-btn.active{background:var(--navy);color:#fff;border-color:var(--navy);}
        .bl-tag-count{font-size:0.68rem;background:rgba(0,0,0,0.08);border-radius:50px;padding:1px 7px;margin-left:4px;}
        .bl-tag-btn.active .bl-tag-count{background:rgba(255,255,255,0.2);}
        .bl-wrap{max-width:1200px;margin:0 auto;padding:70px 24px;}
        .bl-featured{display:grid;grid-template-columns:1.1fr 1fr;gap:0;border-radius:20px;overflow:hidden;box-shadow:var(--sh-lg);margin-bottom:64px;text-decoration:none;color:inherit;transition:transform 0.3s,box-shadow 0.3s;}
        .bl-featured:hover{transform:translateY(-6px);box-shadow:0 32px 80px rgba(11,28,107,0.2);}
        .bl-featured-img{position:relative;min-height:420px;overflow:hidden;}
        .bl-featured-img img{width:100%;height:100%;object-fit:cover;transition:transform 0.5s;}
        .bl-featured:hover .bl-featured-img img{transform:scale(1.04);}
        .bl-featured-body{padding:48px 44px;background:var(--white);display:flex;flex-direction:column;justify-content:center;}
        .bl-feat-label{display:inline-flex;align-items:center;gap:8px;font-size:0.7rem;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--sky);margin-bottom:16px;}
        .bl-feat-label span{display:inline-block;width:28px;height:2px;background:var(--sky);}
        .bl-featured-body h2{font-size:clamp(1.4rem,2.2vw,2rem);font-weight:800;color:var(--navy);line-height:1.25;margin-bottom:16px;}
        .bl-featured-body p{font-size:0.92rem;color:var(--mid);line-height:1.8;font-weight:300;margin-bottom:24px;display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden;}
        .bl-feat-meta{display:flex;align-items:center;gap:16px;flex-wrap:wrap;margin-bottom:24px;}
        .bl-feat-meta span{font-size:0.78rem;color:var(--mid);font-weight:400;}
        .bl-feat-meta span strong{color:var(--navy);font-weight:600;}
        .bl-feat-tags{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:28px;}
        .bl-pill{font-size:0.72rem;font-weight:600;padding:4px 12px;border-radius:50px;background:var(--lsky);color:var(--sky);}
        .bl-read-btn{display:inline-flex;align-items:center;gap:8px;background:var(--navy);color:#fff;border:none;padding:13px 28px;border-radius:50px;font-family:'Lexend',sans-serif;font-weight:600;font-size:0.88rem;cursor:pointer;transition:background 0.2s,transform 0.2s;align-self:flex-start;}
        .bl-read-btn:hover{background:var(--sky);transform:translateX(4px);}
        .bl-section-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:32px;}
        .bl-section-title{font-size:1.4rem;font-weight:700;color:var(--navy);}
        .bl-count{font-size:0.8rem;color:var(--mid);font-weight:400;}
        .bl-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:28px;}
        .bl-card{background:var(--white);border:1px solid var(--border);border-radius:18px;overflow:hidden;box-shadow:var(--sh-sm);transition:transform 0.3s,box-shadow 0.3s;text-decoration:none;color:inherit;display:flex;flex-direction:column;}
        .bl-card:hover{transform:translateY(-6px);box-shadow:var(--sh-lg);}
        .bl-card-img{height:200px;overflow:hidden;position:relative;}
        .bl-card-img img{width:100%;height:100%;object-fit:cover;transition:transform 0.4s;}
        .bl-card:hover .bl-card-img img{transform:scale(1.06);}
        .bl-card-no-img{height:200px;background:linear-gradient(135deg,var(--navy),var(--sky));display:flex;align-items:center;justify-content:center;font-size:3rem;}
        .bl-card-body{padding:24px;flex:1;display:flex;flex-direction:column;}
        .bl-card-tags{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:12px;}
        .bl-card-title{font-size:1rem;font-weight:700;color:var(--navy);line-height:1.35;margin-bottom:10px;}
        .bl-card-desc{font-size:0.85rem;color:var(--mid);line-height:1.7;font-weight:300;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;flex:1;margin-bottom:18px;}
        .bl-card-footer{display:flex;align-items:center;justify-content:space-between;border-top:1px solid var(--border);padding-top:14px;margin-top:auto;}
        .bl-card-meta-sm{font-size:0.75rem;color:var(--mid);font-weight:400;}
        .bl-card-arrow{width:34px;height:34px;border-radius:50%;background:var(--lsky);color:var(--sky);display:flex;align-items:center;justify-content:center;font-size:0.9rem;transition:all 0.2s;}
        .bl-card:hover .bl-card-arrow{background:var(--navy);color:#fff;}
        .bl-empty{text-align:center;padding:80px 20px;color:var(--mid);}
        .bl-empty-icon{font-size:3.5rem;margin-bottom:16px;}
        .bl-empty h4{font-size:1.1rem;color:var(--navy);margin-bottom:8px;}
        .bl-load-more{text-align:center;margin-top:48px;}
        .bl-load-more-btn{background:var(--white);color:var(--navy);border:2px solid var(--navy);padding:13px 36px;border-radius:50px;font-family:'Lexend',sans-serif;font-weight:600;font-size:0.92rem;cursor:pointer;transition:all 0.2s;}
        .bl-load-more-btn:hover{background:var(--navy);color:#fff;}
        .bl-social{background:linear-gradient(135deg,#0b1c6b,#159EEC);color:white;text-align:center;padding:60px 24px;}
        .bl-social h3{font-size:1.6rem;font-weight:700;margin-bottom:8px;}
        .bl-social p{color:rgba(255,255,255,0.75);font-size:0.93rem;font-weight:300;margin-bottom:32px;}
        .bl-social-links{display:flex;justify-content:center;gap:14px;flex-wrap:wrap;}
        .bl-social-link{display:flex;align-items:center;gap:8px;padding:11px 20px;border-radius:50px;border:1.5px solid rgba(255,255,255,0.3);color:#fff;text-decoration:none;font-size:0.86rem;font-weight:600;background:rgba(255,255,255,0.1);transition:all 0.25s;}
        .bl-social-link:hover{background:rgba(255,255,255,0.25);transform:translateY(-3px);}
        .bl-spinner{display:flex;justify-content:center;align-items:center;padding:80px 20px;}
        .bl-spinner-ring{width:44px;height:44px;border:4px solid var(--lsky);border-top-color:var(--navy);border-radius:50%;animation:blSpin 0.8s linear infinite;}
        @keyframes blSpin{to{transform:rotate(360deg)}}
        @media(max-width:900px){.bl-featured{grid-template-columns:1fr;}.bl-featured-img{min-height:260px;}.bl-featured-body{padding:30px 26px;}}
        @media(max-width:600px){.bl-grid{grid-template-columns:1fr;}.bl-hero{min-height:340px;}}
      `}</style>

      <div className="bl">
        {/* HERO */}
        <section className="bl-hero">
          <div className="bl-hero-content">
            <span className="bl-badge">Gospel City Church International</span>
            <h1>News &amp; Blog</h1>
            <p>Stories of faith, updates from the church family, and teaching to strengthen your walk with God.</p>
            <div className="bl-search-wrap">
              <span className="bl-search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search articles by title or keyword..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* TAG FILTER */}
        <div className="bl-tags-bar">
          <div className="bl-tags-inner">
            <button
              className={`bl-tag-btn ${!activeTag ? "active" : ""}`}
              onClick={() => setActiveTag(null)}
            >
              All <span className="bl-tag-count">{blogs.length}</span>
            </button>
            {allTags.map((t) => (
              <button
                key={t.slug}
                className={`bl-tag-btn ${activeTag === t.slug ? "active" : ""}`}
                onClick={() => setActiveTag(activeTag === t.slug ? null : t.slug)}
              >
                {t.title}
              </button>
            ))}
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="bl-wrap">
          {loading ? (
            <div className="bl-spinner"><div className="bl-spinner-ring" /></div>
          ) : error ? (
            <div className="bl-empty">
              <div className="bl-empty-icon">⚠️</div>
              <h4>Could not load articles</h4>
              <p>{error}</p>
            </div>
          ) : blogs.length === 0 ? (
            <div className="bl-empty">
              <div className="bl-empty-icon">📭</div>
              <h4>No articles found</h4>
              <p>Try a different search term or tag filter.</p>
            </div>
          ) : (
            <>
              {/* Featured */}
              {featured && (
                <Link to={`/blogs/${featured.slug}`} className="bl-featured">
                  <div className="bl-featured-img">
                    {featured.cover_image
                      ? <img src={featured.cover_image} alt={featured.title} />
                      : <div style={{ background: "linear-gradient(135deg,#0b1c6b,#159EEC)", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "4rem" }}>✝️</div>
                    }
                  </div>
                  <div className="bl-featured-body">
                    <div className="bl-feat-label"><span></span>Featured Article</div>
                    <h2>{featured.title}</h2>
                    <p>{extractDescription(featured.descriptionJson)}</p>
                    <div className="bl-feat-meta">
                      <span>📅 <strong>{formatDate(featured.published_at)}</strong></span>
                      <span>⏱ {readTime(extractDescription(featured.descriptionJson))}</span>
                    </div>
                    <div className="bl-feat-tags">
                      {(featured.tags || []).map((t) => (
                        <span key={t.slug} className="bl-pill">{t.title}</span>
                      ))}
                    </div>
                    <button className="bl-read-btn">Read Article →</button>
                  </div>
                </Link>
              )}

              {/* Grid */}
              {secondary.length > 0 && (
                <>
                  <div className="bl-section-header">
                    <span className="bl-section-title">More Articles</span>
                    <span className="bl-count">{secondary.length} article{secondary.length !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="bl-grid">
                    {secondary.map((b) => (
                      <Link to={`/blogs/${b.slug}`} className="bl-card" key={b.id}>
                        <div className="bl-card-img">
                          {b.cover_image
                            ? <img src={b.cover_image} alt={b.title} />
                            : <div className="bl-card-no-img">✝️</div>
                          }
                        </div>
                        <div className="bl-card-body">
                          <div className="bl-card-tags">
                            {(b.tags || []).map((t) => (
                              <span key={t.slug} className="bl-pill">{t.title}</span>
                            ))}
                          </div>
                          <h3 className="bl-card-title">{b.title}</h3>
                          <p className="bl-card-desc">{extractDescription(b.descriptionJson)}</p>
                          <div className="bl-card-footer">
                            <div>
                              <div className="bl-card-meta-sm">📅 {formatDate(b.published_at)}</div>
                              <div className="bl-card-meta-sm">⏱ {readTime(extractDescription(b.descriptionJson))}</div>
                            </div>
                            <div className="bl-card-arrow">→</div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </>
              )}

              {/* Load More */}
              {page < totalPages && (
                <div className="bl-load-more">
                  <button
                    className="bl-load-more-btn"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? "Loading..." : "Load More Articles"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* SOCIAL */}
        <section className="bl-social">
          <h3>Follow Us</h3>
          <p>Stay updated with the latest from Gospel City Church International.</p>
          <div className="bl-social-links">
             <a href="https://groups.imo.im/.5l9akqTOrYa5Tv4T4" target="_blank" rel="noopener noreferrer" className="bl-social-link imo">
            <i className="bi bi-chat-dots fs-4"></i> IMO
            </a>
            <a href="https://groups.imo.im/.5l9akqTOrYa5Tv4T4" target="_blank" rel="noopener noreferrer" className="bl-social-link">
              <i className="bi bi-i"></i> LinkedIn
            </a>
           
            <a href="https://x.com/GospelCityChur?t=x8dsFbFkghmYh02lV0FVxQ&s=03" target="_blank" rel="noopener noreferrer" className="bl-social-link">
              <i className="bi bi-twitter-x"></i> Twitter / X
            </a>
            <a href="https://whatsapp.com/channel/0029Vb769UyDzgTBAZZcb40F" target="_blank" rel="noopener noreferrer" className="bl-social-link">
              <i className="bi bi-whatsapp"></i> WhatsApp
            </a>
            <a href="https://youtube.com/@gospelcitychurchinternational?si=1pZx2ak4ss3b4HuU" target="_blank" rel="noopener noreferrer" className="bl-social-link">
              <i className="bi bi-youtube"></i> YouTube
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogList;